class CalculatorModal {
  constructor() {
    this.modal = null;
    this.currentStep = 'form';
    this.formData = {
      type: 'room',
      windows: 0,
      doors: 0,
      length: 0,
      width: 0,
      height: 0
    };
    this.results = {
      squareFootage: 0,
      paintNeeded: 1,
      primerNeeded: 1
    };
    this.productData = {
      paint: null,
      primer: null,
      kit: null
    };
    this.optionData = {
      paint: null,
      kit: null
    };
    this.currentPaintVariant = null;
    this.coverageConfig = {
      paintCoverage: 300,
      paintCoats: 2,
      primerCoverage: 400,
      currencyCode: 'USD',
      moneyLocale: document.documentElement.lang || 'en-US'
    };
    this.isAddingToCart = false;

    this.loadProductData();
    this.init();
  }

  init() {
    this.createModal();
    this.attachEventListeners();
  }

  loadProductData() {
    try {
      const paintScript = document.getElementById('calculator-paint');
      const primerScript = document.getElementById('calculator-primer');
      const kitScript = document.getElementById('calculator-kit');
      const configScript = document.getElementById('calculator-config');
      const optionsScript = document.getElementById('calculator-options');

      if (paintScript) {
        this.productData.paint = JSON.parse(paintScript.textContent);
      }

      if (primerScript) {
        this.productData.primer = JSON.parse(primerScript.textContent);
      }

      if (kitScript) {
        this.productData.kit = JSON.parse(kitScript.textContent);
      }

      if (configScript) {
        const config = JSON.parse(configScript.textContent);
        this.coverageConfig = {
          paintCoverage: Number(config.paintCoverage) || 300,
          paintCoats: Number(config.paintCoats) || 2,
          primerCoverage: Number(config.primerCoverage) || 400,
          currencyCode: config.currencyCode || 'USD',
          moneyLocale: config.moneyLocale || document.documentElement.lang || 'en-US'
        };
      }

      if (optionsScript) {
        const optionData = JSON.parse(optionsScript.textContent);
        this.optionData.paint = optionData.paint || null;
        this.optionData.kit = optionData.kit || null;
      }

      this.currentPaintVariant = this.findPreferredPaintVariant(this.getPdpSelections());
    } catch (error) {
      console.error('Error loading product data:', error);
    }
  }

  getPdpSelections() {
    const hero = document.querySelector('.product-hero');
    const selectedColor = hero?.querySelector('.is-selected[data-option-type="color"][data-option-value]');
    const selectedFinish = hero?.querySelector('.is-selected[data-option-type="finish"][data-option-value]');

    return {
      color: selectedColor?.dataset.optionValue || '',
      finish: selectedFinish?.dataset.optionValue || ''
    };
  }

  getVariants(productType) {
    const normalizedVariants = this.optionData[productType]?.variants;
    if (Array.isArray(normalizedVariants) && normalizedVariants.length) {
      return normalizedVariants;
    }

    const productVariants = this.productData[productType]?.variants || [];
    return productVariants.map(variant => ({
      ...variant,
      options: {
        '1': variant.option1,
        '2': variant.option2,
        '3': variant.option3
      }
    }));
  }

  getOptionValue(productType, variant, optionType) {
    const position = this.optionData[productType]?.[`${optionType}OptionPosition`];
    if (!position || !variant) return '';
    return variant.options?.[String(position)] || variant[`option${position}`] || '';
  }

  findPaintVariant({ color = '', finish = '', availableOnly = true } = {}) {
    return this.getVariants('paint').find(variant => {
      if (availableOnly && !variant.available) return false;

      const variantColor = this.getOptionValue('paint', variant, 'color');
      const variantFinish = this.getOptionValue('paint', variant, 'finish');
      return (!color || variantColor === color) && (!finish || variantFinish === finish);
    }) || null;
  }

  findPreferredPaintVariant({ color = '', finish = '' } = {}) {
    return this.findPaintVariant({ color, finish })
      || this.findPaintVariant({ color })
      || this.findPaintVariant({ finish })
      || this.findPaintVariant()
      || this.getVariants('paint')[0]
      || null;
  }

  getSelectedPaintOptions() {
    return {
      color: this.getOptionValue('paint', this.currentPaintVariant, 'color'),
      finish: this.getOptionValue('paint', this.currentPaintVariant, 'finish')
    };
  }

  findKitVariant() {
    const { color, finish } = this.getSelectedPaintOptions();
    const kitData = this.optionData.kit || {};

    return this.getVariants('kit').find(variant => {
      if (!variant.available) return false;

      const variantColor = this.getOptionValue('kit', variant, 'color');
      const variantFinish = this.getOptionValue('kit', variant, 'finish');
      const colorMatches = !kitData.colorOptionPosition || !color || variantColor === color;
      const finishMatches = !kitData.finishOptionPosition || !finish || variantFinish === finish;

      return colorMatches && finishMatches;
    }) || null;
  }

  createModal() {
    const modalHTML = `
      <div class="calculator-modal" id="calculator-modal">
        <div class="calculator-modal__content">
          <button class="calculator-modal__close" data-action="close-modal"></button>

          <!-- Step 1: Form -->
          <div class="calculator-modal__step calculator-modal__step--form is-active" data-step="form">
            <h2 class="calculator-modal__title">Paint coverage calculator</h2>

            <div class="calculator-modal__divider"></div>

            <div class="calculator-modal__section-title">I'm painting a:</div>

            <div class="calculator-modal__radio-group">
              <label class="calculator-modal__radio-option">
                <input type="radio" name="paintType" value="room" class="calculator-modal__radio-input" checked data-field="type">
                <span class="calculator-modal__radio-visual"></span>
                <span class="calculator-modal__radio-label">Room</span>
              </label>
              <label class="calculator-modal__radio-option">
                <input type="radio" name="paintType" value="wall" class="calculator-modal__radio-input" data-field="type">
                <span class="calculator-modal__radio-visual"></span>
                <span class="calculator-modal__radio-label">Wall</span>
              </label>
            </div>

            <div class="calculator-modal__section-title calculator-modal__windows-doors-title">
              How many windows and doors are in your room?
            </div>

            <div class="calculator-modal__form-row">
              <div class="calculator-modal__field-group">
                <div class="calculator-modal__select-field">
                  <label class="calculator-modal__field-label" for="windows-select">Windows</label>
                  <select class="calculator-modal__select" id="windows-select" data-field="windows">
                    <option value="">select</option>
                    ${Array.from({length: 11}, (_, i) => `
                      <option value="${i}">${i}</option>
                    `).join('')}
                  </select>
                </div>
              </div>
              <div class="calculator-modal__field-group">
                <div class="calculator-modal__select-field">
                  <label class="calculator-modal__field-label" for="doors-select">Doors</label>
                  <select class="calculator-modal__select" id="doors-select" data-field="doors">
                    <option value="">select</option>
                    ${Array.from({length: 10}, (_, i) => `
                      <option value="${i + 1}">${i + 1}</option>
                    `).join('')}
                  </select>
                </div>
              </div>
            </div>

            <div class="calculator-modal__section-title calculator-modal__dimensions-title">
              Enter your room's dimensions
            </div>

            <div class="calculator-modal__form-row calculator-modal__dimensions-row">
              <div class="calculator-modal__field-group">
                <div class="calculator-modal__input-field">
                  <div class="calculator-modal__field-label">Length</div>
                  <div class="calculator-modal__input-wrapper">
                    <input type="number" class="calculator-modal__input" placeholder="0" data-field="length" min="0" step="0.1">
                    <span class="calculator-modal__unit">ft.</span>
                  </div>
                </div>
              </div>
              <div class="calculator-modal__field-group">
                <div class="calculator-modal__input-field">
                  <div class="calculator-modal__field-label">Width</div>
                  <div class="calculator-modal__input-wrapper">
                    <input type="number" class="calculator-modal__input" placeholder="0" data-field="width" min="0" step="0.1">
                    <span class="calculator-modal__unit">ft.</span>
                  </div>
                </div>
              </div>
              <div class="calculator-modal__field-group">
                <div class="calculator-modal__input-field">
                  <div class="calculator-modal__field-label">Height</div>
                  <div class="calculator-modal__input-wrapper">
                    <input type="number" class="calculator-modal__input" placeholder="0" data-field="height" min="0" step="0.1">
                    <span class="calculator-modal__unit">ft.</span>
                  </div>
                </div>
              </div>
            </div>

            <button class="calculator-modal__calculate-btn" data-action="calculate">Calculate</button>
          </div>

          <!-- Step 2: Results -->
          <div class="calculator-modal__step calculator-modal__step--results" data-step="results">
            <div class="calculator-modal__results-header">
              <button class="calculator-modal__back-btn" data-action="back-to-form"></button>
              <h2 class="calculator-modal__results-title">What you'll need</h2>
            </div>
            <button class="calculator-modal__reset-link" data-action="reset">Reset</button>

            <div class="calculator-modal__divider"></div>

            <div class="calculator-modal__results-summary">
              <span class="calculator-modal__results-text"></span>
            </div>

            <div class="calculator-modal__products">
              ${this.generateProductCard('primer')}
              ${this.generateProductCard('paint')}
            </div>

            <button class="calculator-modal__add-to-cart" data-action="add-to-cart">Add To Cart · $175</button>
          </div>
        </div>
      </div>
    `;

    document.body.insertAdjacentHTML('beforeend', modalHTML);
    this.modal = document.getElementById('calculator-modal');
    this.applyAllSwatches();
    this.syncVariantControls();
  }

  generateProductCard(productType) {
    const product = this.productData[productType];

    if (!product) {
      return `<div class="calculator-modal__product-card">Product data not available</div>`;
    }

    const defaultVariant = product.variants?.[0];
    const productImage = product.featured_image || defaultVariant?.featured_image;
    const imageUrl = productImage ? productImage.replace(/\.(jpg|jpeg|png|gif|webp)/, '_300x300.$1') : '';

    const variantOptions = productType === 'paint' ? this.generatePaintOptions() : '';

    return `
      <div class="calculator-modal__product-card" data-product-card="${productType}">
        <div class="calculator-modal__product-image">
          ${imageUrl
            ? `<img src="${this.escapeAttribute(imageUrl)}" alt="${this.escapeAttribute(product.title)}" loading="lazy" />`
            : `<div class="calculator-modal__product-image-placeholder">${this.escapeHtml(product.title)}</div>`
          }
        </div>
        <div class="calculator-modal__product-details">
          <h3 class="calculator-modal__product-name">${this.escapeHtml(product.title)}</h3>
          <div class="calculator-modal__product-options">
            ${variantOptions}
            <div class="calculator-modal__quantity-selector" data-product="${productType}">
              <button type="button" class="calculator-modal__quantity-btn calculator-modal__quantity-btn--minus" data-action="decrease-quantity" aria-label="Decrease ${this.escapeAttribute(product.title)} quantity"></button>
              <span class="calculator-modal__quantity-value">1</span>
              <button type="button" class="calculator-modal__quantity-btn calculator-modal__quantity-btn--plus" data-action="increase-quantity" aria-label="Increase ${this.escapeAttribute(product.title)} quantity"></button>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  generatePaintOptions() {
    const paintData = this.optionData.paint || {};
    const colors = Array.isArray(paintData.colors) ? paintData.colors : [];
    const finishes = Array.isArray(paintData.finishes) ? paintData.finishes : [];
    const { color: selectedColor, finish: selectedFinish } = this.getSelectedPaintOptions();
    const selectedColorData = colors.find(color => color.name === selectedColor) || colors[0] || null;

    const colorOptions = colors.map(color => {
      const state = this.getColorState(color.name, selectedFinish);
      const isSelected = color.name === selectedColor;

      return `
        <button
          type="button"
          class="calculator-modal__color-option${isSelected ? ' is-selected' : ''}${state.comingSoon ? ' is-coming-soon' : ''}"
          data-action="select-color"
          data-color="${this.escapeAttribute(color.name)}"
          aria-pressed="${isSelected ? 'true' : 'false'}"
          ${state.hidden ? 'hidden' : ''}
          ${state.available ? '' : 'disabled'}
        >
          <span
            class="calculator-modal__color-swatch"
            data-swatch-color="${this.escapeAttribute(color.swatchColor || '')}"
            data-swatch-image="${this.escapeAttribute(color.swatchImage || '')}"
            aria-hidden="true"
          ></span>
          <span class="calculator-modal__color-copy">
            <span class="calculator-modal__color-name">${this.escapeHtml(color.name)}</span>
          </span>
          ${state.comingSoon ? '<span class="calculator-modal__color-badge">Coming soon</span>' : ''}
        </button>
      `;
    }).join('');

    const finishOptions = finishes.map(finish => {
      const state = this.getFinishState(finish.name, selectedColor);
      const isSelected = finish.name === selectedFinish;

      return `
        <button
          type="button"
          class="calculator-modal__finish-option${isSelected ? ' is-selected' : ''}"
          data-action="select-finish"
          data-finish="${this.escapeAttribute(finish.name)}"
          aria-pressed="${isSelected ? 'true' : 'false'}"
          ${state.hidden ? 'hidden' : ''}
          ${state.available ? '' : 'disabled'}
        >
          <span class="calculator-modal__finish-swatch${isSelected ? ' is-selected' : ''}" aria-hidden="true"></span>
          <span class="calculator-modal__finish-text">${this.escapeHtml(finish.name)}</span>
        </button>
      `;
    }).join('');

    const colorSelector = colors.length ? `
      <div class="calculator-modal__color-selector">
        <div class="calculator-modal__option-label">Color</div>
        <button
          type="button"
          class="calculator-modal__color-trigger"
          data-action="toggle-color-menu"
          aria-expanded="false"
        >
          <span
            class="calculator-modal__color-swatch"
            data-selected-color-swatch
            data-swatch-color="${this.escapeAttribute(selectedColorData?.swatchColor || '')}"
            data-swatch-image="${this.escapeAttribute(selectedColorData?.swatchImage || '')}"
            aria-hidden="true"
          ></span>
          <span class="calculator-modal__color-trigger-copy">
            <span class="calculator-modal__color-trigger-name" data-selected-color-name>${this.escapeHtml(selectedColor)}</span>
          </span>
          <span class="calculator-modal__color-chevron" aria-hidden="true"></span>
        </button>
        <div class="calculator-modal__color-menu" data-color-menu hidden>
          ${colorOptions}
        </div>
      </div>
    ` : '';

    const finishSelector = finishes.length ? `
      <div class="calculator-modal__finish-selector">
        <div class="calculator-modal__option-label">Finish</div>
        <div class="calculator-modal__finish-options">
          ${finishOptions}
        </div>
      </div>
    ` : '';

    return colorSelector + finishSelector;
  }

  getColorState(color, finish) {
    const matchingVariants = this.getVariants('paint').filter(variant => (
      this.getOptionValue('paint', variant, 'color') === color
      && (!finish || this.getOptionValue('paint', variant, 'finish') === finish)
    ));
    const hasAvailableVariant = matchingVariants.some(variant => variant.available);
    const hasComingSoonVariant = matchingVariants.some(variant => variant.comingSoon);
    const showUnavailableColors = Boolean(this.optionData.paint?.showUnavailableColors);

    return {
      hidden: matchingVariants.length === 0
        || (!hasAvailableVariant && !hasComingSoonVariant && !showUnavailableColors),
      available: hasAvailableVariant,
      comingSoon: matchingVariants.length > 0 && !hasAvailableVariant && hasComingSoonVariant
    };
  }

  getFinishState(finish, color) {
    const matchingVariants = this.getVariants('paint').filter(variant => (
      this.getOptionValue('paint', variant, 'finish') === finish
      && (!color || this.getOptionValue('paint', variant, 'color') === color)
    ));

    return {
      hidden: matchingVariants.length === 0,
      available: matchingVariants.some(variant => variant.available)
    };
  }

  getProductPrice(productType) {
    const variant = this.getCurrentVariant(productType);
    if (!variant) return 0;

    // Price is usually in cents, convert to dollars
    return variant.price / 100;
  }

  calculateKitQuantities(paintNeeded, primerNeeded) {
    // Calculate how many kits we need based on the minimum of paint and primer needed
    const kitQuantity = Math.min(paintNeeded, primerNeeded);

    // Calculate remaining individual products needed
    const remainingPaint = paintNeeded - kitQuantity;
    const remainingPrimer = primerNeeded - kitQuantity;

    return {
      kitQuantity,
      remainingPaint,
      remainingPrimer
    };
  }

  calculatePricing(paintQuantity, primerQuantity) {
    const paintPrice = this.getProductPrice('paint');
    const primerPrice = this.getProductPrice('primer');
    const kitVariant = this.getCurrentVariant('kit');
    const kitPrice = kitVariant ? kitVariant.price / 100 : 0;

    const originalPrice = (paintQuantity * paintPrice) + (primerQuantity * primerPrice);
    const quantities = kitVariant
      ? this.calculateKitQuantities(paintQuantity, primerQuantity)
      : {
          kitQuantity: 0,
          remainingPaint: paintQuantity,
          remainingPrimer: primerQuantity
        };
    const { kitQuantity, remainingPaint, remainingPrimer } = quantities;
    const actualPrice = (kitQuantity * kitPrice) + (remainingPaint * paintPrice) + (remainingPrimer * primerPrice);

    return {
      originalPrice,
      actualPrice,
      kitQuantity,
      remainingPaint,
      remainingPrimer
    };
  }

  getCurrentVariant(productType) {
    const product = this.productData[productType];
    if (!product || !product.variants) return null;

    if (productType === 'paint') {
      return this.currentPaintVariant?.available
        ? this.currentPaintVariant
        : null;
    }

    if (productType === 'kit') {
      return this.findKitVariant();
    }

    return product.variants.find(variant => variant.available) || null;
  }

  updateTotalPrice() {
    if (!this.modal) return;

    const paintQuantity = parseInt(this.modal.querySelector('[data-product="paint"] .calculator-modal__quantity-value')?.textContent) || 0;
    const primerQuantity = parseInt(this.modal.querySelector('[data-product="primer"] .calculator-modal__quantity-value')?.textContent) || 0;

    const pricing = this.calculatePricing(paintQuantity, primerQuantity);
    const paintVariant = this.getCurrentVariant('paint');
    const primerVariant = this.getCurrentVariant('primer');

    const addToCartBtn = this.modal.querySelector('.calculator-modal__add-to-cart');
    if (addToCartBtn) {
      if (pricing.actualPrice < pricing.originalPrice) {
        addToCartBtn.innerHTML = `Add To Cart · <span class="calculator-modal__price--strike">${this.formatMoney(pricing.originalPrice * 100)}</span> ${this.formatMoney(pricing.actualPrice * 100)}`;
      } else {
        addToCartBtn.textContent = `Add To Cart · ${this.formatMoney(pricing.actualPrice * 100)}`;
      }

      addToCartBtn.disabled = (paintQuantity > 0 && !paintVariant) || (primerQuantity > 0 && !primerVariant);
    }
  }

  attachEventListeners() {
    // Close modal events
    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal || e.target.dataset.action === 'close-modal') {
        this.closeModal();
      }
    });

    // Form field changes (radio buttons and select elements)
    this.modal.addEventListener('change', (e) => {
      if (e.target.dataset.field === 'type') {
        this.handleTypeChange(e.target.value);
      } else if (e.target.dataset.field && (e.target.dataset.field === 'windows' || e.target.dataset.field === 'doors')) {
        this.updateFormData(e.target.dataset.field, parseInt(e.target.value) || 0);
      }
    });

    // Input field changes
    this.modal.addEventListener('input', (e) => {
      if (e.target.dataset.field) {
        this.updateFormData(e.target.dataset.field, parseFloat(e.target.value) || 0);
      }
    });

    // Action buttons
    this.modal.addEventListener('click', (e) => {
      // Find the closest element with data-action attribute
      const actionElement = e.target.closest('[data-action]');
      if (!actionElement) return;

      const action = actionElement.dataset.action;
      switch (action) {
        case 'calculate':
          this.calculate();
          break;
        case 'reset':
          this.reset();
          break;
        case 'back-to-form':
          this.backToForm();
          break;
        case 'increase-quantity':
          this.adjustQuantity(actionElement, 1);
          break;
        case 'decrease-quantity':
          this.adjustQuantity(actionElement, -1);
          break;
        case 'add-to-cart':
          this.addToCart();
          break;
        case 'toggle-color-menu':
          this.toggleColorMenu();
          break;
        case 'select-color':
          this.selectColor(actionElement.dataset.color);
          break;
        case 'select-finish':
          this.selectFinish(actionElement.dataset.finish);
          break;
      }
    });

    document.addEventListener('click', (e) => {
      if (!this.modal?.classList.contains('is-open')) return;
      if (e.target.closest('.calculator-modal__color-selector')) return;
      this.closeColorMenu();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.classList.contains('is-open')) {
        const colorMenu = this.modal.querySelector('[data-color-menu]');
        if (colorMenu && !colorMenu.hidden) {
          this.closeColorMenu();
        } else {
          this.closeModal();
        }
      }
    });
  }

  openModal() {
    this.currentPaintVariant = this.findPreferredPaintVariant(this.getPdpSelections());
    this.syncVariantControls();
    this.updateResultsText();
    this.updateTotalPrice();
    this.modal.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  closeModal() {
    this.closeColorMenu();
    this.modal.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  handleTypeChange(type) {
    this.formData.type = type;

    const windowsDoorsTitle = this.modal.querySelector('.calculator-modal__windows-doors-title');
    const dimensionsTitle = this.modal.querySelector('.calculator-modal__dimensions-title');
    const lengthField = this.modal.querySelector('[data-field="length"]').closest('.calculator-modal__field-group');

    if (type === 'wall') {
      windowsDoorsTitle.textContent = 'How many windows and doors are on this wall?';
      dimensionsTitle.textContent = 'Enter your wall\'s dimensions';
      lengthField.style.display = 'none';
      this.formData.length = 0;
    } else {
      windowsDoorsTitle.textContent = 'How many windows and doors are in your room?';
      dimensionsTitle.textContent = 'Enter your room\'s dimensions';
      lengthField.style.display = 'block';
    }
  }

  updateFormData(field, value) {
    this.formData[field] = value;
  }

  calculate() {
    // Validate required fields
    const { type, width, height, length, windows, doors } = this.formData;

    if (!width || !height || (type === 'room' && !length)) {
      this.showError('Please fill in all dimension fields.');
      return;
    }

    let totalArea;

    if (type === 'wall') {
      // WALL FORMULA: Total Area = Width * Height - 15 * Windows - 25 * Doors
      totalArea = width * height - (15 * windows) - (25 * doors);
    } else {
      // ROOM FORMULA:
      // Ceiling Area = Length * Width
      // Wall Area = 2 * (Length + Width) * Height - 15 * Windows - 25 * Doors
      // Total Area = Ceiling Area + Wall Area
      const ceilingArea = length * width;
      const wallArea = 2 * (length + width) * height - (15 * windows) - (25 * doors);
      totalArea = ceilingArea + wallArea;
    }

    // Deductions can never produce a negative paintable area.
    totalArea = Math.max(0, totalArea);
    this.results.squareFootage = Math.round(totalArea);

    const effectivePaintCoverage = this.coverageConfig.paintCoverage / this.coverageConfig.paintCoats;
    this.results.primerNeeded = totalArea > 0
      ? Math.ceil(totalArea / this.coverageConfig.primerCoverage)
      : 0;
    this.results.paintNeeded = totalArea > 0
      ? Math.ceil(totalArea / effectivePaintCoverage)
      : 0;

    this.showResults();
  }

  showResults() {
    this.updateResultsText();

    // Update quantity selectors
    const paintQuantity = this.modal.querySelector('[data-product="paint"] .calculator-modal__quantity-value');
    const primerQuantity = this.modal.querySelector('[data-product="primer"] .calculator-modal__quantity-value');

    paintQuantity.textContent = this.results.paintNeeded;
    primerQuantity.textContent = this.results.primerNeeded;

    // Update pricing
    this.updateTotalPrice();

    // Switch to results step
    this.switchStep('results');
  }

  updateResultsText() {
    const resultsText = this.modal?.querySelector('.calculator-modal__results-text');
    if (!resultsText) return;

    const areaText = this.formData.type === 'room' ? 'room' : 'one wall';
    const color = this.getSelectedPaintOptions().color;
    const paintDescription = color ? `${this.escapeHtml(color)} paint` : 'paint';
    const paintGallons = this.results.paintNeeded;
    const primerGallons = this.results.primerNeeded;

    resultsText.innerHTML = `You're painting ${areaText} that's about <strong>${this.results.squareFootage} sq. ft.</strong> of surface area. We recommend <strong>${paintGallons} gallon${paintGallons === 1 ? '' : 's'}</strong> of ${paintDescription} and <strong>${primerGallons} gallon${primerGallons === 1 ? '' : 's'}</strong> of primer.`;
  }

  switchStep(step) {
    this.currentStep = step;

    const steps = this.modal.querySelectorAll('.calculator-modal__step');
    steps.forEach(stepEl => {
      stepEl.classList.toggle('is-active', stepEl.dataset.step === step);
    });
  }

  reset() {
    // Reset form data
    this.formData = {
      type: 'room',
      windows: 0,
      doors: 0,
      length: 0,
      width: 0,
      height: 0
    };

    // Reset form UI
    const roomRadio = this.modal.querySelector('[value="room"]');
    roomRadio.checked = true;
    this.handleTypeChange('room');

    // Reset select elements
    const selectElements = this.modal.querySelectorAll('.calculator-modal__select');
    selectElements.forEach(select => {
      select.value = '';
    });

    // Reset inputs
    const inputs = this.modal.querySelectorAll('.calculator-modal__input');
    inputs.forEach(input => {
      input.value = '';
    });

    this.currentPaintVariant = this.findPreferredPaintVariant(this.getPdpSelections());
    this.syncVariantControls();

    // Switch back to form step
    this.switchStep('form');
  }

  backToForm() {
    // Simply switch back to the form step without resetting data
    this.switchStep('form');
  }

  selectColor(color) {
    const { finish } = this.getSelectedPaintOptions();
    const nextVariant = this.findPaintVariant({ color, finish })
      || this.findPaintVariant({ color });

    if (!nextVariant) return;

    this.currentPaintVariant = nextVariant;
    this.closeColorMenu();
    this.syncVariantControls();
    this.updateResultsText();
    this.updateTotalPrice();
  }

  selectFinish(finish) {
    const { color } = this.getSelectedPaintOptions();
    const nextVariant = this.findPaintVariant({ color, finish })
      || this.findPaintVariant({ finish });

    if (!nextVariant) return;

    this.currentPaintVariant = nextVariant;
    this.syncVariantControls();
    this.updateResultsText();
    this.updateTotalPrice();
  }

  toggleColorMenu() {
    const trigger = this.modal?.querySelector('[data-action="toggle-color-menu"]');
    const menu = this.modal?.querySelector('[data-color-menu]');
    if (!trigger || !menu) return;

    const shouldOpen = menu.hidden;
    menu.hidden = !shouldOpen;
    trigger.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
  }

  closeColorMenu() {
    const trigger = this.modal?.querySelector('[data-action="toggle-color-menu"]');
    const menu = this.modal?.querySelector('[data-color-menu]');
    if (!trigger || !menu) return;

    menu.hidden = true;
    trigger.setAttribute('aria-expanded', 'false');
  }

  syncVariantControls() {
    if (!this.modal || !this.currentPaintVariant) return;

    const { color: selectedColor, finish: selectedFinish } = this.getSelectedPaintOptions();
    const colors = this.optionData.paint?.colors || [];
    const selectedColorData = colors.find(color => color.name === selectedColor) || null;

    this.modal.querySelectorAll('[data-action="select-color"]').forEach(button => {
      const state = this.getColorState(button.dataset.color, selectedFinish);
      const isSelected = button.dataset.color === selectedColor;

      button.hidden = state.hidden;
      button.disabled = !state.available;
      button.classList.toggle('is-selected', isSelected);
      button.classList.toggle('is-coming-soon', state.comingSoon);
      button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });

    this.modal.querySelectorAll('[data-action="select-finish"]').forEach(button => {
      const state = this.getFinishState(button.dataset.finish, selectedColor);
      const isSelected = button.dataset.finish === selectedFinish;

      button.hidden = state.hidden;
      button.disabled = !state.available;
      button.classList.toggle('is-selected', isSelected);
      button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
      button.querySelector('.calculator-modal__finish-swatch')?.classList.toggle('is-selected', isSelected);
    });

    const selectedName = this.modal.querySelector('[data-selected-color-name]');
    const selectedSwatch = this.modal.querySelector('[data-selected-color-swatch]');

    if (selectedName) {
      selectedName.textContent = selectedColor;
    }

    if (selectedSwatch) {
      selectedSwatch.dataset.swatchColor = selectedColorData?.swatchColor || '';
      selectedSwatch.dataset.swatchImage = selectedColorData?.swatchImage || '';
      this.applySwatch(selectedSwatch);
    }
  }

  adjustQuantity(button, change) {
    const quantitySelector = button.closest('.calculator-modal__quantity-selector');
    const quantityValue = quantitySelector.querySelector('.calculator-modal__quantity-value');
    const currentValue = parseInt(quantityValue.textContent);
    const newValue = Math.max(0, currentValue + change);

    quantityValue.textContent = newValue;

    // Update total price
    this.updateTotalPrice();
  }

  applyAllSwatches() {
    this.modal?.querySelectorAll('[data-swatch-color], [data-swatch-image]').forEach(node => {
      this.applySwatch(node);
    });
  }

  applySwatch(node) {
    if (!node) return;

    const image = node.dataset.swatchImage || '';
    const color = node.dataset.swatchColor || '';
    node.style.backgroundImage = image ? `url("${image.replace(/["\\]/g, '\\$&')}")` : '';
    node.style.backgroundColor = !image && color ? color : '';
  }

  formatMoney(cents) {
    const amount = (Number(cents) || 0) / 100;
    const isWholeAmount = Number.isInteger(amount);

    try {
      return new Intl.NumberFormat(this.coverageConfig.moneyLocale, {
        style: 'currency',
        currency: this.coverageConfig.currencyCode,
        minimumFractionDigits: isWholeAmount ? 0 : 2,
        maximumFractionDigits: 2
      }).format(amount);
    } catch (error) {
      return `$${isWholeAmount ? amount.toFixed(0) : amount.toFixed(2)}`;
    }
  }

  escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  escapeAttribute(value) {
    return this.escapeHtml(value);
  }

  showError(message = 'An error occurred. Please try again.') {
    // Try to use cart drawer's error notification if available
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && cartDrawer.showError) {
      cartDrawer.showError(message);
      return;
    }

    // Fallback: create our own notification
    let errorNotification = document.querySelector('.calculator-modal__error-notification');

    if (!errorNotification) {
      errorNotification = document.createElement('div');
      errorNotification.className = 'calculator-modal__error-notification';
      errorNotification.setAttribute('role', 'alert');
      errorNotification.setAttribute('aria-live', 'polite');

      errorNotification.style.cssText = `
        position: fixed;
        top: 20px;
        left: 50%;
        transform: translateX(-50%);
        background: #dc3545;
        color: white;
        padding: 12px 24px;
        border-radius: 4px;
        font-size: 14px;
        z-index: 10002;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        opacity: 0;
        transition: opacity 0.3s ease;
        max-width: 90%;
        text-align: center;
      `;

      document.body.appendChild(errorNotification);
    }

    errorNotification.textContent = message;
    errorNotification.style.opacity = '1';

    clearTimeout(this.errorNotificationTimeout);
    this.errorNotificationTimeout = setTimeout(() => {
      errorNotification.style.opacity = '0';
    }, 4000);
  }

  async addToCart() {
    // Prevent multiple simultaneous add to cart requests
    if (this.isAddingToCart) {
      return;
    }

    this.isAddingToCart = true;

    // Get current selections and quantities
    const paintQuantity = parseInt(this.modal.querySelector('[data-product="paint"] .calculator-modal__quantity-value').textContent) || 0;
    const primerQuantity = parseInt(this.modal.querySelector('[data-product="primer"] .calculator-modal__quantity-value').textContent) || 0;

    const paintVariant = this.getCurrentVariant('paint');
    const primerVariant = this.getCurrentVariant('primer');
    const kitVariant = this.getCurrentVariant('kit');

    const { kitQuantity, remainingPaint, remainingPrimer } = this.calculatePricing(paintQuantity, primerQuantity);

    // Build items array for cart
    const items = [];

    // Add kits if needed
    if (kitQuantity > 0 && kitVariant && kitVariant.id) {
      items.push({
        id: parseInt(kitVariant.id),
        quantity: kitQuantity
      });
    }

    // Add remaining paint if needed
    if (remainingPaint > 0 && paintVariant && paintVariant.id) {
      items.push({
        id: parseInt(paintVariant.id),
        quantity: remainingPaint
      });
    }

    // Add remaining primer if needed
    if (remainingPrimer > 0 && primerVariant && primerVariant.id) {
      items.push({
        id: parseInt(primerVariant.id),
        quantity: remainingPrimer
      });
    }

    // Check if we have items to add
    if (items.length === 0) {
      console.warn('No items to add to cart');
      this.showError('Please select at least one product to add to cart.');
      this.isAddingToCart = false;
      return;
    }

    // Get the cart drawer element
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) {
      console.error('Cart drawer element not found');
      this.showError('Unable to add to cart. Please refresh the page.');
      this.isAddingToCart = false;
      return;
    }

    // Show loading state on add to cart button
    const addToCartBtn = this.modal.querySelector('.calculator-modal__add-to-cart');
    const originalBtnHTML = addToCartBtn?.innerHTML || '';

    if (addToCartBtn) {
      addToCartBtn.innerHTML = 'Adding to Cart...';
      addToCartBtn.disabled = true;
    }

    try {
      // Add items to cart and open drawer
      await cartDrawer.addItemsAndOpen(items);

      // Re-enable button before closing modal
      if (addToCartBtn) {
        addToCartBtn.innerHTML = originalBtnHTML;
        addToCartBtn.disabled = false;
      }

      // Close the calculator modal after successful addition
      this.closeModal();

    } catch (error) {
      console.error('Error adding items to cart:', error);
      this.showError(error.message || 'Failed to add items to cart.');

      // Show error state briefly
      if (addToCartBtn) {
        addToCartBtn.innerHTML = 'Error - Try Again';
        setTimeout(() => {
          addToCartBtn.innerHTML = originalBtnHTML;
          addToCartBtn.disabled = false;
        }, 2000);
      }
    } finally {
      // Always reset the flag
      this.isAddingToCart = false;
    }
  }
}

// Initialize the calculator modal when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  window.calculatorModal = new CalculatorModal();
});

// Export for use in other scripts
if (typeof module !== 'undefined' && module.exports) {
  module.exports = CalculatorModal;
}
